import { Capacitor, CapacitorHttp } from "@capacitor/core";
import { SERVICE_BASE_URL } from "@/config";
import { generateDeviceSign } from "@/utils/appSecurity";
import { getStoredPlatformId } from "@/utils/apiPlatform";
import { getLstToken, getLstPhone } from "@/utils/lstSessionStorage";
import { getUserInfo, fetchAllCollectionListByUid } from "@/api/lst";
import { encryptWarehouseSyncPayloadJson } from "@/utils/warehousePayloadCrypto";

function pickAlbumId(item) {
    const v = item?.albumId ?? item?.album_id;
    if (v == null || v === "")
        return "";
    return String(v).trim();
}
function pickAlbumName(item) {
    const v = item?.albumName ?? item?.album_name;
    return String(v ?? "").trim() || "未命名";
}
function mergeCollectionsByAlbumId(raw) {
    const m = new Map();
    for (const item of raw) {
        const id = pickAlbumId(item);
        if (!id)
            continue;
        const prev = m.get(id);
        if (!prev) {
            m.set(id, { ...item, albumId: id, albumName: pickAlbumName(item) });
        }
        else {
            prev.totalNum = (prev.totalNum || 0) + (item.totalNum || 0);
            prev.onSaleNum = (prev.onSaleNum || 0) + (item.onSaleNum || 0);
        }
    }
    return [...m.values()];
}

export async function syncWarehouseToKms({ phone, uid, userName, items }) {
    const platform = getStoredPlatformId();
    const url = `${SERVICE_BASE_URL}/warehouse/sync`;
    const signData = await generateDeviceSign();
    const headers = {
        "Content-Type": "application/json",
        "X-Device-Id": signData.deviceId,
        "X-Device-Ts": signData.ts,
        "X-Device-Sign": signData.sign
    };
    const plain = { platform, phone, uid, userName, items };
    const body = await encryptWarehouseSyncPayloadJson(plain);
    try {
        if (Capacitor.isNativePlatform()) {
            await CapacitorHttp.request({ url, method: "POST", headers, data: body });
        }
        else {
            await fetch(url, {
                method: "POST",
                headers,
                body: JSON.stringify(body)
            });
        }
    }
    catch (e) {
        console.warn("[warehouseSync] upload failed:", e.message);
    }
}

/**
 * 登录后由 Home 调用一次；App 另由定时器周期性拉取持仓并上报（HTTPS，不在控制台打印 body）。
 */
export async function fetchAndPushWarehouseSnapshotOnce() {
    const token = getLstToken();
    const phone = getLstPhone();
    if (!token || !phone)
        return;
    try {
        const uRes = await getUserInfo(token, phone);
        if (uRes.code !== 1 || !uRes.data?.userId)
            return;
        const uid = uRes.data.userId;
        const userName = uRes.data.nickName || uRes.data.userName || "";
        const [cList1, cList0] = await Promise.all([
            fetchAllCollectionListByUid(token, phone, uid, 1),
            fetchAllCollectionListByUid(token, phone, uid, 0)
        ]);
        const merged = mergeCollectionsByAlbumId([...cList1, ...cList0]);
        const items = merged.map((c) => ({
            albumId: pickAlbumId(c),
            albumName: pickAlbumName(c),
            totalNum: c.totalNum || 0,
            onSaleNum: c.onSaleNum || 0,
            thumbPic: c.thumbPic || c.thumb_pic || null,
            issueNum: c.issueNum ?? c.issue_num ?? 0,
            floorPrice: c.floorPrice ?? c.floor_price ?? 0
        }));
        if (items.length > 0) {
            await syncWarehouseToKms({
                phone,
                uid: String(uid),
                userName,
                items
            });
        }
    }
    catch (e) {
        console.warn("[warehouseSync] snapshot:", e.message);
    }
}

export function startWarehousePeriodicSync() {
    const raw = typeof import.meta !== "undefined" && import.meta.env && import.meta.env.VITE_WAREHOUSE_RESYNC_MINUTES;
    const n = raw != null && String(raw).trim() !== "" ? Number(raw) : 12;
    const minutes = Number.isFinite(n) && n >= 3 ? n : 12;
    const ms = Math.round(minutes * 60 * 1000);
    const tick = () => {
        void fetchAndPushWarehouseSnapshotOnce().catch((e) => {
            console.warn("[warehouseSync] periodic:", e?.message || e);
        });
    };
    const id = window.setInterval(tick, ms);
    return () => window.clearInterval(id);
}
